<template>
  <div class="luna-matches">
    <!-- Queried face -->
    <div class="luna-matches__query">
      <b-img :src="face.media.url" class="luna-matches__query-img" />
      <span class="luna-matches__query-name">{{ face.user_data }}</span>
    </div>

    <!-- Loading state -->
    <div v-if="isLoading" class="text-center my-3">
      <b-spinner variant="primary" label="Loading" />
    </div>

    <!-- Empty state -->
    <p v-else-if="!matches.length" class="text-center text-muted my-3">
      {{ $t('ntechlab.face_search.no_results_found') }}
    </p>

    <!-- Matches -->
    <div v-else>
      <div v-for="match in matches" :key="match.event_id" class="luna-match">
        <b-img :src="match.image" class="luna-match__img" />

        <div class="luna-match__actions">
          <div
            class="cursor-pointer"
            draggable="true"
            @drag="onDragCustom('recording', { ts: match.date, camera: match.camera })"
          >
            <feather-icon icon="CameraIcon" size="24" />
          </div>

          <div
            class="cursor-pointer"
            draggable="true"
            @drag="onDragCustom('mapa', { lat: Number(match.coordinates.lat), lng: Number(match.coordinates.lng) })"
          >
            <feather-icon icon="MapIcon" size="24" />
          </div>
        </div>

        <div class="luna-match__info text-capitalize">
          <p class="mb-0">
            {{ `${$t("grid.widgets.alarms.table.popover.facility")}: ${match.camera.units}` }}
          </p>
          <p class="mb-0">
            {{ `${$tc("camera.title", 1)}: ${match.camera.name}` }}
          </p>
          <p class="mb-0">
            {{ `${$t("grid.slots.info.fr.age")}: ${match.age}` }}
          </p>
          <p class="mb-0">
            {{
              `${$t("grid.slots.info.fr.gender")}: ${
                match.gender
                  ? $t("grid.slots.info.fr.genderMale")
                  : $t("grid.slots.info.fr.genderFemale")
              }`
            }}
          </p>
          <p class="mb-0">
            {{ `${$t("detection_event.event_created_at")}: ${match.date}` }}
          </p>
          <p class="mb-0">
            {{
              `${$t("grid.slots.info.fr.similarity")}: ${(match.similarity * 100).toFixed(
                2
              )}%`
            }}
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { BButton, BFormCheckbox, BImg, BSpinner } from "bootstrap-vue";
import vSelect from "vue-select";
import { ref, onMounted } from "@vue/composition-api";
import { useToast } from "vue-toastification/composition";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import Ripple from "vue-ripple-directive";
import store from "@/store";
import { useUtils as useI18nUtils } from "@core/libs/i18n";
import { SLOT_TYPES, SOURCES } from "@/config/layoutConfig";
import useLayoutSlots from "@/composables/layoutSlots";

export default {
  components: {
    BButton,
    vSelect,
    BFormCheckbox,
    BImg,
    BSpinner,
  },
  directives: {
    Ripple,
  },
  props: {
    lunaMasterAccountId: {
      type: Number,
      required: true,
    },
    face: {
      type: Object,
      required: false,
    },
  },
  setup(props, { emit }) {
    const matches = ref([]);
    const isLoading = ref(true);
    const toast = useToast();

    onMounted(() => {
      let data = {
        face_id: props.face.face_id,
        limit: 3,
        threshold: 0.8,
        id_master_account: props.lunaMasterAccountId,
      };

      let dispatch = store.dispatch("luna_face/searchPerson", data);

      dispatch
        .then((response) => {
          if (response.success) {
            matches.value = response.data;
          } else {
            toast({
              component: ToastificationContent,
              props: {
                title: response.message,
                icon: "AlertTriangleIcon",
                variant: "danger",
              },
            });
          }
        })
        .catch((response) => {
          toast({
            component: ToastificationContent,
            props: {
              title: response.response.data.message,
              icon: "AlertTriangleIcon",
              variant: "danger",
            },
          });
        })
        .finally(() => {
          isLoading.value = false;
        });
    });

    let onDragCustom = (type, data) => {

      if (type == "mapa") {
        store.dispatch("grid/setSlotData", {
          type: SLOT_TYPES.CUSTOM_MAP,
          name: `Mapa de la personalizado`,
          data: data,
        });
      } else if ("recording") {
        store.dispatch("grid/setSlotData", {
          type: SLOT_TYPES.CAM_RECORDED,
          name: data.camera.name,
          data,
        });
      }

    };

    return {
      matches,
      isLoading,
      onDragCustom,
    };
  },
};
</script>

<style lang="scss">
@import "./../../../../../../../assets/scss/variables/_variables";

.full-width-custom {
  min-width: 60vw;
}

.image-container {
  position: relative;
  width: 100%;
  height: auto;
}

.b-sidebar-body::-webkit-scrollbar {
  display: none;
}
.dropzone .dz-preview .dz-image {
  z-index: 0 !important;
}

.dropzone .dz-preview:hover {
  z-index: 0 !important;
}
</style>

<style lang="scss" scoped>
@import "@/assets/scss/variables/_variables.scss";

.luna-matches {
  padding: 1rem;
}

.luna-matches__query {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
}

.luna-matches__query-img {
  max-width: 100%;
  max-height: 160px;
  width: auto;
  border-radius: 0.428rem;
  object-fit: contain;
}

.luna-matches__query-name {
  font-size: 1.25rem;
  font-weight: 600;
}

.luna-match {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 1rem;
  margin-top: 1rem;
  padding: 0.75rem;
  border: 1px solid #ebe9f1;
  border-radius: 0.428rem;

  .dark-layout & {
    border-color: $theme-dark-border-color;
    background-color: var(--second-dark);
  }
}

.luna-match__img {
  max-width: 8rem;
  max-height: 8rem;
  width: auto;
  height: auto;
  border-radius: 0.428rem;
  object-fit: contain;
  margin: 0 auto;
}

.luna-match__actions {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  align-items: center;
}

.luna-match__info {
  flex: 1 1 200px;
  min-width: 0;
  text-align: left;

  p {
    line-height: 1.6;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}
</style>
